/**
 * Performance Monitor
 * Enhanced with AI usage tracking and provider health monitoring
 */

interface PerformanceMetric {
  name: string;
  duration: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface AIUsageMetric {
  provider: string;
  model: string;
  latency: number;
  success: boolean;
  attempts: number;
  error?: string;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private aiMetrics: AIUsageMetric[] = [];
  private slowQueryThreshold = 2000; // 2 seconds
  private activeTimers = new Map<string, number>();

  /**
   * Start timing an operation
   */
  start(operationName: string): void {
    this.activeTimers.set(operationName, performance.now());
  }

  /**
   * End timing and record metric
   */
  end(operationName: string, metadata?: Record<string, any>): number {
    const startTime = this.activeTimers.get(operationName);
    if (!startTime) {
      console.warn(`[Performance] No start time found for operation: ${operationName}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.activeTimers.delete(operationName);

    this.recordMetric({
      name: operationName,
      duration,
      timestamp: Date.now(),
      metadata
    });

    return duration;
  }

  /**
   * Track database query performance
   */
  trackDBQuery(queryName: string, duration: number, table?: string): void {
    if (duration > this.slowQueryThreshold) {
      console.warn(`[Perf] Slow DB query detected (${duration.toFixed(2)}ms): ${queryName}${table ? ` on ${table}` : ''}`);
    }

    this.recordMetric({
      name: `db_${queryName}`,
      duration,
      timestamp: Date.now(),
      metadata: { table, type: 'database' }
    });
  }

  /**
   * Track AI usage and performance
   */
  trackAIUsage(metric: AIUsageMetric): void {
    this.aiMetrics.push(metric);

    // Log slow AI responses
    if (metric.latency > 10000) { // 10 seconds
      console.warn(`[Perf] Slow AI response (${metric.latency}ms): ${metric.provider}/${metric.model}`);
    }

    // Log failed requests
    if (!metric.success) {
      console.error(`[Perf] AI request failed: ${metric.provider}/${metric.model} - ${metric.error}`);
    }

    // Log multiple attempts (fallback usage)
    if (metric.attempts > 1) {
      console.info(`[Perf] AI fallback used: ${metric.attempts} attempts for ${metric.provider}/${metric.model}`);
    }

    this.recordMetric({
      name: `ai_${metric.provider}_${metric.model}`,
      duration: metric.latency,
      timestamp: Date.now(),
      metadata: { 
        type: 'ai',
        provider: metric.provider,
        model: metric.model,
        success: metric.success,
        attempts: metric.attempts,
        error: metric.error
      }
    });

    // Keep only last 100 AI metrics in memory
    if (this.aiMetrics.length > 100) {
      this.aiMetrics = this.aiMetrics.slice(-100);
    }
  }

  /**
   * Set slow query threshold
   */
  setSlowQueryThreshold(threshold: number): void {
    this.slowQueryThreshold = threshold;
  }

  /**
   * Get performance summary
   */
  getSummary(): {
    totalMetrics: number;
    averageLatency: number;
    slowQueries: number;
    aiUsage: {
      totalRequests: number;
      successRate: number;
      averageLatency: number;
      providerBreakdown: Record<string, { requests: number; successRate: number; avgLatency: number }>;
    };
  } {
    const dbMetrics = this.metrics.filter(m => m.metadata?.type === 'database');
    const aiMetrics = this.metrics.filter(m => m.metadata?.type === 'ai');
    
    const slowQueries = dbMetrics.filter(m => m.duration > this.slowQueryThreshold).length;
    const averageLatency = this.metrics.length > 0 
      ? this.metrics.reduce((sum, m) => sum + m.duration, 0) / this.metrics.length 
      : 0;

    // AI usage summary
    const aiSuccessful = this.aiMetrics.filter(m => m.success);
    const aiSuccessRate = this.aiMetrics.length > 0 
      ? (aiSuccessful.length / this.aiMetrics.length) * 100 
      : 0;
    const aiAvgLatency = aiSuccessful.length > 0
      ? aiSuccessful.reduce((sum, m) => sum + m.latency, 0) / aiSuccessful.length
      : 0;

    // Provider breakdown
    const providerBreakdown: Record<string, { requests: number; successRate: number; avgLatency: number }> = {};
    
    for (const metric of this.aiMetrics) {
      if (!providerBreakdown[metric.provider]) {
        providerBreakdown[metric.provider] = { requests: 0, successRate: 0, avgLatency: 0 };
      }
      providerBreakdown[metric.provider].requests++;
    }

    for (const [provider, stats] of Object.entries(providerBreakdown)) {
      const providerMetrics = this.aiMetrics.filter(m => m.provider === provider);
      const successful = providerMetrics.filter(m => m.success);
      
      stats.successRate = (successful.length / providerMetrics.length) * 100;
      stats.avgLatency = successful.length > 0
        ? successful.reduce((sum, m) => sum + m.latency, 0) / successful.length
        : 0;
    }

    return {
      totalMetrics: this.metrics.length,
      averageLatency,
      slowQueries,
      aiUsage: {
        totalRequests: this.aiMetrics.length,
        successRate: aiSuccessRate,
        averageLatency: aiAvgLatency,
        providerBreakdown
      }
    };
  }

  /**
   * Get recent metrics
   */
  getRecentMetrics(limit = 50): PerformanceMetric[] {
    return this.metrics.slice(-limit);
  }

  /**
   * Get AI provider health
   */
  getAIProviderHealth(): Record<string, {
    status: 'healthy' | 'degraded' | 'unhealthy';
    successRate: number;
    averageLatency: number;
    lastError?: string;
    lastSuccess?: number;
  }> {
    const health: Record<string, any> = {};
    const recentWindow = Date.now() - (15 * 60 * 1000); // Last 15 minutes
    
    const recentAIMetrics = this.aiMetrics.filter(m => 
      this.metrics.find(metric => 
        metric.metadata?.provider === m.provider && 
        metric.timestamp > recentWindow
      )
    );

    const providers = [...new Set(recentAIMetrics.map(m => m.provider))];
    
    for (const provider of providers) {
      const providerMetrics = recentAIMetrics.filter(m => m.provider === provider);
      const successful = providerMetrics.filter(m => m.success);
      const failed = providerMetrics.filter(m => !m.success);
      
      const successRate = providerMetrics.length > 0 
        ? (successful.length / providerMetrics.length) * 100 
        : 0;
      
      const avgLatency = successful.length > 0
        ? successful.reduce((sum, m) => sum + m.latency, 0) / successful.length
        : 0;

      let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
      if (successRate < 50) {
        status = 'unhealthy';
      } else if (successRate < 80 || avgLatency > 30000) {
        status = 'degraded';
      }

      const lastError = failed.length > 0 ? failed[failed.length - 1].error : undefined;
      const lastSuccessMetric = successful[successful.length - 1];
      const lastSuccess = lastSuccessMetric 
        ? this.metrics.find(m => 
            m.metadata?.provider === provider && 
            m.duration === lastSuccessMetric.latency
          )?.timestamp
        : undefined;

      health[provider] = {
        status,
        successRate,
        averageLatency: avgLatency,
        lastError,
        lastSuccess
      };
    }

    return health;
  }

  /**
   * Get DB-specific summary grouped by table
   */
  getDBSummary(): Record<string, { avg: number; min: number; max: number; count: number; slowQueries: number }> {
    const dbMetrics = this.metrics.filter(m => m.metadata?.type === 'database');
    const result: Record<string, { avg: number; min: number; max: number; count: number; slowQueries: number }> = {};

    for (const m of dbMetrics) {
      const key = m.metadata?.table || m.name;
      if (!result[key]) result[key] = { avg: 0, min: Infinity, max: 0, count: 0, slowQueries: 0 };
      const r = result[key];
      r.count++;
      r.min = Math.min(r.min, m.duration);
      r.max = Math.max(r.max, m.duration);
      r.avg = r.avg + (m.duration - r.avg) / r.count;
      if (m.duration > this.slowQueryThreshold) r.slowQueries++;
    }

    // Fix Infinity for tables with no entries
    for (const key of Object.keys(result)) {
      if (result[key].min === Infinity) result[key].min = 0;
    }

    return result;
  }

  /**
   * Get slow queries report
   */
  getSlowQueriesReport(): { query: string; duration: number; timestamp: number }[] {
    return this.metrics
      .filter(m => m.duration > this.slowQueryThreshold)
      .map(m => ({ query: m.name, duration: m.duration, timestamp: m.timestamp }))
      .sort((a, b) => b.duration - a.duration);
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    this.aiMetrics = [];
    this.activeTimers.clear();
  }

  /**
   * Log summary to console
   */
  logSummary(): void {
    const summary = this.getSummary();
    console.log('[Performance Summary]', summary);
  }

  /**
   * Clear old metrics to prevent memory leaks
   */
  cleanup(): void {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    this.metrics = this.metrics.filter(m => m.timestamp > oneHourAgo);
    
    // Keep AI metrics for longer (24 hours) for better health monitoring
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const recentAIMetrics = this.aiMetrics.filter(m => {
      const metric = this.metrics.find(metric => 
        metric.metadata?.provider === m.provider && 
        metric.duration === m.latency
      );
      return metric && metric.timestamp > oneDayAgo;
    });
    this.aiMetrics = recentAIMetrics;
  }

  private recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    
    // Keep only last 1000 metrics in memory
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Auto-cleanup every hour
setInterval(() => {
  performanceMonitor.cleanup();
}, 60 * 60 * 1000);